import { defineStore } from "pinia"
import { ref, computed } from "vue"
import router from "@/router/index"
import HomeService from "@/services/resources/HomeService"
import CategoryService from "@/services/resources/CategoryService"
import CourseService from "@/services/resources/CourseService"
import scrollToElement from "@/utils/scroll-to-element"
import { toast } from "@/events"
import { useAuthStore } from "@/store/auth.store"
import { paths } from "@/utils/route-paths"
import { NO_CATEGORY } from "@/utils/constants"

/* Services */
const serviceHome = HomeService.build()
const serviceCategory = CategoryService.build()
const serviceCourse = CourseService.build()

/* Types */
export interface ModalPayload<T = Record<string, unknown>> {
  active: boolean
  data: T
}

export interface CourseCategory {
  id: number | null
  title?: string
  order?: number
  courses?: Course[]
  [key: string]: unknown
}

export interface Course {
  id: number | string
  order?: number
  category?: CourseCategory | null
  status?: string
  has_access?: boolean
  is_liberated?: boolean
  rule_liberation?: unknown
  liberated_at?: string
  cover?: string
  title?: string
  [key: string]: unknown
}

export interface HomeData {
  courses: Course[]
  categories: CourseCategory[]
  [key: string]: unknown
}

export interface ModalBuyCourseData {
  id?: number | string
  title?: string
  cover?: string
  button?: string
  description?: string
  link?: string
  video?: string
  video_thumb?: string
  video_type?: string
  [key: string]: unknown
}

export interface ModalInfoCourseData {
  rule?: unknown
  date?: string
  type?: number
  [key: string]: unknown
}

export const useHomeLegacyStore = defineStore('home-legacy', () => {
  /* State */
  const modalInfoCourse = ref<ModalPayload<ModalInfoCourseData>>({
    active: false,
    data: {},
  })
  const modalBuyCourse = ref<ModalPayload<ModalBuyCourseData>>({
    active: false,
    data: {},
  })
  const openedAccordionCategories = ref<number>(0)
  const rearrange = ref<boolean>(false)
  const allCategories = ref<CourseCategory[]>([])
  const allCourses = ref<Course[]>([])
  const homeData = ref<HomeData>({
    courses: [],
    categories: [],
  })
  const loadingModule = ref<boolean>(false)
  const emptyState = ref<unknown>(null)
  const tourGuideCompleted = ref<boolean>(true)

  /* Getters (computed) */
  const getHomeData = computed(() => homeData.value)

  const getAllCategories = computed(() => {
    const courses = homeData.value?.courses
    if (!courses) return undefined

    const categoriasUnicas: Record<string | number, CourseCategory> = {}

    courses.forEach((curso) => {
      const category = curso.category || NO_CATEGORY
      const id = category.id as string | number
      if (!categoriasUnicas[id]) {
        categoriasUnicas[id] = Object.assign({}, category) as CourseCategory
      }
    })

    const categoriasUnicasArray = Object.values(categoriasUnicas)
    return categoriasUnicasArray.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  })

  const getOpenedAccordionCategories = computed(() => openedAccordionCategories.value)
  const getModalInfoCourse = computed(() => modalInfoCourse.value)
  const getModalBuyCourse = computed(() => modalBuyCourse.value)
  const getRearrangeCourse = computed(() => rearrange.value)
  const getCoursesList = computed(() => allCourses.value)
  const getLoadingModule = computed(() => loadingModule.value)
  const getEmptyState = computed(() => emptyState.value)
  const getTourGuideStatus = computed(() => tourGuideCompleted.value)

  /* Mutations as actions */
  function setRearrange() {
    rearrange.value = !rearrange.value
  }

  function setHomeData(response: HomeData) {
    homeData.value = response
  }

  function updateCoursesList(payload: Course[]) {
    allCourses.value = Array.from(payload)
  }

  function setCategoriesData(categories: CourseCategory[]) {
    allCategories.value = categories
  }

  function setOpenAccordionCategories(index: number) {
    openedAccordionCategories.value = index
  }

  function showModalInfo(payload: ModalPayload<ModalInfoCourseData>) {
    modalInfoCourse.value = payload
  }

  function showModalBuy(payload: ModalPayload<ModalBuyCourseData>) {
    modalBuyCourse.value = payload
  }

  function setLoadingModule(value: boolean) {
    loadingModule.value = value
  }

  function setEmptyState(response: unknown) {
    emptyState.value = response
  }

  function setTourGuideStatus(completed: boolean) {
    tourGuideCompleted.value = completed
  }

  /* Async actions */
  function actionUpdateCoursesList(payload: Course[]) {
    updateCoursesList(payload)
  }

  function actionToggleRearrangeCourse() {
    setRearrange()
  }

  function actionShowModalInfoCourse(payload: ModalPayload<ModalInfoCourseData>) {
    showModalInfo(payload)
  }

  function actionShowModalBuyCourse(payload: ModalPayload<ModalBuyCourseData>) {
    showModalBuy(payload)
  }

  async function disableEmptyState(key: string) {
    const result = await serviceHome.disableEmptyState(key)
    setEmptyState(result)
  }

  async function actionRearrangeOrderCourse(array: unknown[]) {
    try {
      const data = {
        id: "/order",
        array,
      }
      const response = await serviceCourse.postIDArray(data)
      return response
    } catch (error) {
      toast.error("Erro ao Salvar Ordenação!")
      throw error
    }
  }

  async function actionFetchCourseAvailability(id: number | string) {
    const keys =
      "/meta?keys[]=sale_link&keys[]=sale_video&keys[]=sale_video_thumb&keys[]=sale_button_txt&keys[]=sale_description&keys[]=sale_video_type"
    const description = await serviceCourse.read(id + keys)
    return { ...description }
  }

  async function actionFetchCategoriesData() {
    const response = await serviceCategory.search()
    setCategoriesData(response)
    return response
  }

  async function actionFetchHomeData(payload?: { page?: number }) {
    const response = await serviceHome.search(`page=${payload?.page ?? 1}`)

    if (!emptyState.value && homeData.value?.courses?.length === 0) {
      const emptyStateResult = await serviceHome.getEmptyState()
      setEmptyState(emptyStateResult)
    }

    const mergedCourses = (payload?.page ?? 1) > 1
      ? [
          ...(homeData.value?.courses || []),
          ...response.courses,
        ]
      : response.courses

    setHomeData({
      ...response,
      courses: mergedCourses,
    })

    const sortCourses = [
      ...new Set(mergedCourses),
    ].sort((a: Course, b: Course) => (a.order ?? 0) - (b.order ?? 0))

    updateCoursesList(sortCourses)

    return response
  }

  async function actionFetchSidebar() {
    // no-op (commented out in legacy)
  }

  async function actionOpenedAccordionCategories(number: number) {
    try {
      setOpenAccordionCategories(number)
      if (!number) return

      setLoadingModule(true)

      const categoriesInHomeData = homeData.value.categories
      const existingCategory = categoriesInHomeData.find((category) => category.id == number)?.courses

      if (existingCategory) {
        const categories = getAllCategories.value
        const emptyCategory = categories?.find((item) => item.id == number)

        if (emptyCategory === undefined) return

        scrollToElement("accordion-" + number)
        setLoadingModule(false)
        return
      }

      const response = await serviceHome.read("/getCoursesByCategory/" + number)

      const authStore = useAuthStore()
      const courses = authStore.isAdmin()
        ? response
        : response.filter((c: Course) => c.status === 'published')

      const updatedCategories = homeData.value.categories.map((category) => {
        if (category.id === number && !category.courses) {
          return { ...category, courses }
        }
        return category
      })

      setHomeData({ ...homeData.value, categories: updatedCategories })

      if (response.length === 0) return

      if (response.length > 0) {
        scrollToElement("accordion-" + number)
      }

      setLoadingModule(false)
    } catch (_error) {
      setLoadingModule(false)
    }
  }

  async function actionOpenedAccordionCategoriesNoScroll(number: number) {
    try {
      setOpenAccordionCategories(number)
      if (!number) return

      setLoadingModule(true)

      if (
        homeData.value.categories.find(
          (category) => category.id == number
        )?.courses
      ) {
        setLoadingModule(false)
        return
      }

      const response = await serviceHome.read("/getCoursesByCategory/" + number)

      const authStore = useAuthStore()
      const courses = authStore.isAdmin()
        ? response
        : response.filter((c: Course) => c.status === 'published')

      setHomeData({
        ...homeData.value,
        categories: homeData.value.categories.map((category) => {
          if (category.id == number && !category.courses) {
            return { ...category, courses }
          } else {
            return category
          }
        }),
      })

      setLoadingModule(false)
    } catch (_error) {
      setLoadingModule(false)
    }
  }

  async function actionNavToCourse(course: Course) {
    if (course.has_access === false) {
      const response = await actionFetchCourseAvailability(course?.id)

      const data: ModalBuyCourseData = {
        id: response.id,
        title: response.title,
        cover: response.cover,
        button: response.sale_button_txt,
        description: response.sale_description,
        link: response.sale_link,
        video: response.sale_video,
        video_thumb: response.sale_video_thumb,
        video_type: response.sale_video_type,
      }

      actionShowModalBuyCourse({
        active: true,
        data,
      })
      return
    }

    if (course.has_access === true && course.is_liberated === false) {
      const data: ModalInfoCourseData = {
        rule: course.rule_liberation,
        date: course.liberated_at,
        type: 1,
      }

      actionShowModalInfoCourse({
        active: true,
        data,
      })
      return
    }

    return router.push({ path: paths.watch(course.id)! })
  }

  async function saveTourGuideStatus(completed: boolean) {
    try {
      const response = await serviceHome.saveTourGuideStatus(completed)
      setTourGuideStatus(completed)
      return response?.data ?? response
    } catch (error) {
      console.error('Error saving tour guide status:', error)
      throw error
    }
  }

  async function fetchTourGuideStatus() {
    try {
      const response = await serviceHome.getTourGuideStatus()
      setTourGuideStatus(response)
      return response
    } catch (error) {
      console.error('Error fetching tour guide status:', error)
      throw error
    }
  }

  function $reset() {
    modalInfoCourse.value = { active: false, data: {} }
    modalBuyCourse.value = { active: false, data: {} }
    openedAccordionCategories.value = 0
    rearrange.value = false
    allCategories.value = []
    allCourses.value = []
    homeData.value = { courses: [], categories: [] }
    loadingModule.value = false
    emptyState.value = null
    tourGuideCompleted.value = true
  }

  return {
    /* State */
    modalInfoCourse,
    modalBuyCourse,
    openedAccordionCategories,
    rearrange,
    allCategories,
    allCourses,
    homeData,
    loadingModule,
    emptyState,
    tourGuideCompleted,

    /* Getters */
    getHomeData,
    getAllCategories,
    getOpenedAccordionCategories,
    getModalInfoCourse,
    getModalBuyCourse,
    getRearrangeCourse,
    getCoursesList,
    getLoadingModule,
    getEmptyState,
    getTourGuideStatus,

    /* Actions */
    setRearrange,
    setHomeData,
    updateCoursesList,
    setCategoriesData,
    setOpenAccordionCategories,
    showModalInfo,
    showModalBuy,
    setLoadingModule,
    setEmptyState,
    setTourGuideStatus,
    actionUpdateCoursesList,
    actionToggleRearrangeCourse,
    actionShowModalInfoCourse,
    actionShowModalBuyCourse,
    disableEmptyState,
    actionRearrangeOrderCourse,
    actionFetchCourseAvailability,
    actionFetchCategoriesData,
    actionFetchHomeData,
    actionFetchSidebar,
    actionOpenedAccordionCategories,
    actionOpenedAccordionCategoriesNoScroll,
    actionNavToCourse,
    saveTourGuideStatus,
    fetchTourGuideStatus,
    $reset,
  }
})
